import * as React from 'react';
import { useTranslation } from 'react-i18next';
import clsx from 'clsx';
import moment from 'moment-timezone';

import { Review } from 'models/review';
import { getReviewAttributionText } from 'lib/util/getReviewAttributionText';

import { convertReviewRatingToNumber } from './review.utils';
import { Stars } from '../Stars/Stars';
import { ImageSlider } from './ImageSlider';
import { ProductReviewBody } from './ProductReviewBody';
import { ProductReviewSupplierComments } from './ProductReviewSupplierComments';
import styles from './ProductReviewsPane.module.css';

import { BookingWidgetThemeContext } from 'contexts/BookingWidgetThemeContext';
import { getThemeCssOverride } from 'components/Themes';

interface Props {
  review: Review;
}

export const ReviewCard = ({ review }: Props) => {
  const { t } = useTranslation();

  const ratingAsNumber = convertReviewRatingToNumber(review.rating);

  const bookingWidgetTheme = React.useContext(BookingWidgetThemeContext);

  return (
    <li className={styles['c-review__list__comment__item']}>
      <div className={styles['c-review__list__comment__item__top']}>
        <div className={styles['c-review__list__comment__item__top__review']}>
          <div className={styles['c-review__list__comment__item__top__review__stars']}>
            <Stars rating={ratingAsNumber} />
          </div>
          <p className={styles['c-review__list__comment__item__top__review__num']}>
            {ratingAsNumber}
          </p>
        </div>
        <div className={styles['c-review__list__comment__item__top__info']}>
          <p className={styles['c-review__list__comment__item__top__info__date']}>
            {t('Participation Date: {{participationDate}}', {
              participationDate: review.participation_date_time_local.substring(0, 7),
            })}
          </p>
          <p
            className={clsx(
              styles['c-review__list__comment__item__top__info__type'],
              getThemeCssOverride(
                bookingWidgetTheme,
                'c-review__list__comment__item__top__info__type'
              )
            )}
          >
            {getReviewAttributionText(review.attribution, t)}
          </p>
        </div>
      </div>
      <p className={styles['c-review__list__comment__item__ttl']}>{review.title}</p>
      <ProductReviewBody review={review} />
      {review.media_items != null && review.media_items.length > 0 && (
        <ImageSlider imageUrls={review.media_items?.map((mediaItem) => mediaItem.url) ?? []} />
      )}
      <div className={styles['c-review__list__comment__item__user']}>
        <p className={styles['c-review__list__comment__item__user__ic']}>
          {review.guest_nickname.substring(0, 1)}
        </p>
        <div className={styles['c-review__list__comment__item__user__info']}>
          <p className={styles['c-review__list__comment__item__user__info__name']}>
            {review.guest_nickname}
          </p>
          <p className={styles['c-review__list__comment__item__user__info__date']}>
            {t('Posted Date: {{postedDate}}', {
              postedDate: moment(review.written_date_time_utc).format('YYYY/MM/DD'),
            })}
          </p>
        </div>
      </div>
      <ProductReviewSupplierComments review={review} />
    </li>
  );
};
